import { ChevronDown } from 'lucide-react';
import React from 'react';
import {
  Select as AriaSelect,
  SelectProps as AriaSelectProps,
  Button,
  ListBox,
  ListBoxItemProps,
  SelectValue,
  ValidationResult
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { Description, FieldError, Label } from '~/components/Field';
import { DropdownItem, DropdownSection, DropdownSectionProps } from '~/components/ListBox';
import { Popover } from '~/components/Popover';
import { composeTailwindRenderProps, focusRing } from '~/library/utilities';

const styles = tv({
  extend: focusRing,
  base: 'u-flex u-items-center u-text-start u-h-20 u-gap-4 u-w-full u-border-2 u-border-blue u-pl-3 u-pr-2 u-py-2',
  variants: {
    isDisabled: {
      false: 'u-text-blue',
      true: 'u-text-black/50'
    }
  }
});

export interface SelectProps<T extends object> extends Omit<AriaSelectProps<T>, 'children'> {
  label?: string;
  description?: string;
  icon?: React.ReactNode;
  errorMessage?: string | ((validation: ValidationResult) => string);
  items?: Iterable<T>;
  children: React.ReactNode | ((item: T) => React.ReactNode);
}

export function Select<T extends object>(
  { label, description, icon, errorMessage, children, items, ...props }: SelectProps<T>
) {
  return (
    <AriaSelect {...props} className={composeTailwindRenderProps(props.className, 'u-group u-flex u-flex-col u-gap-1 u-relative')}>
      {label && <Label className="u-sr-only">{label}</Label>}
      <Button className={styles}>
        {icon}
        <SelectValue className="u-flex-1" />
        <ChevronDown aria-hidden className="u-w-8 u-h-8 u-text-pink" />
      </Button>
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
      <Popover className="u-min-w-[--trigger-width] u-max-w-[--trigger-width] sm:u-max-w-full">
        <ListBox items={items} className="u-outline-none u-p-1 u-max-h-[inherit] u-overflow-auto [clip-path:inset(0_0_0_0_round_.75rem)]">
          {children}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}

export function SelectItem(props: ListBoxItemProps) {
  return <DropdownItem {...props} />;
}

export function SelectSection<T extends object>(props: DropdownSectionProps<T>) {
  return <DropdownSection {...props} />;
}
